import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, computed, input } from '@angular/core';
import { IonContent, IonicModule } from '@ionic/angular';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { TranslatePipe } from '@ngx-translate/core';

/** One entry of the legal pages' section index. */
export interface LegalTocSection {
  /** DOM id of the section card this entry scrolls to. */
  id: string;
  /** Short marker rendered left of the label ('01', '§ 4', …). */
  num: string;
  /** i18n key of the section heading. Ignored when `title` is set. */
  titleKey?: string;
  /**
   * Ready-made heading, used instead of `titleKey`. The product legal documents
   * (main/legal/) carry their own text per language — including fr/it, which the
   * portal-wide translation files do not cover — so they cannot go through i18n keys.
   */
  title?: string;
  /**
   * Heading of the product (or "general") block this entry belongs to, as plain text.
   * Consecutive entries with the same group share one heading in the index.
   * Entries without a group stay in an unlabelled block.
   */
  group?: string;
  /** i18n key of the group heading. Ignored when `group` is set. */
  groupKey?: string;
  /** DOM id the group heading scrolls to (e.g. the product's header card). Defaults to the group's first entry. */
  groupTarget?: string;
}

/** A labelled run of consecutive index entries. */
export interface LegalTocGroup {
  label?: string;
  labelKey?: string;
  /** DOM id the heading scrolls to. */
  target?: string;
  sections: readonly LegalTocSection[];
}

/** Folds the flat entry list into groups; only neighbours with the same heading are merged. */
export function groupTocSections(sections: readonly LegalTocSection[]): LegalTocGroup[] {
  const groups: { id: string; group: LegalTocGroup; sections: LegalTocSection[] }[] = [];
  for (const section of sections) {
    const id = section.group ?? section.groupKey ?? '';
    const last = groups[groups.length - 1];
    if (last && last.id === id) {
      last.sections.push(section);
      continue;
    }
    const entries = [section];
    groups.push({
      id,
      sections: entries,
      group: {
        label: section.group,
        labelKey: section.group ? undefined : section.groupKey,
        target: section.groupTarget ?? section.id,
        sections: entries,
      },
    });
  }
  return groups.map(g => g.group);
}

/**
 * Sticky section index for the long legal pages (privacy, terms). Clicking an entry
 * scrolls its section into view and the entry tracks the scroll position.
 *
 * The host page must pass its <ion-content> in: these pages scroll inside ion-content's
 * own shadow-DOM scroll container, not the window, so neither `#fragment` anchors nor
 * window.scrollTo would move anything here.
 */
@Component({
    selector: 'app-legal-toc',
    templateUrl: './legal-toc.component.html',
    styleUrls: ['./legal-toc.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IonicModule,
        LoadingSpinnerComponent,
        TranslatePipe,
    ],
})
export class LegalTocComponent {
  readonly sections = input<readonly LegalTocSection[]>([]);

  /** The entries folded into labelled blocks (one per product, plus the general block). */
  readonly groups = computed(() => groupTocSections(this.sections()));

  /** Index heading, used instead of the i18n default when the page renders a
   *  product legal document in a language the portal UI itself does not ship. */
  readonly heading = input<string>();

  /** The host page's <ion-content> — the real scroll container (see class comment). */
  readonly content = input<IonContent>();

  /** Pixels left free above the scrolled-to section, e.g. for the 64px fixed app-portal-navbar. */
  readonly scrollOffset = input(16);

  /** Currently scrolled-past section, highlighted in the list. */
  activeId: string | null = null;

  /** Below the sidebar breakpoint the list collapses behind a toggle; the CSS keeps it
   *  open unconditionally on wide screens, so this only matters on narrow ones. */
  open = false;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  async scrollTo(section: LegalTocSection): Promise<void> {
    this.open = false;
    await this.scrollToId(section.id);
  }

  /**
   * Scrolls to any section id, TOC entry or not — used for the annex cards (which have
   * a DOM id but no TOC entry) and for the host page's initial `#fragment` deep link.
   * A plain `#fragment` cannot do this itself: these pages scroll inside ion-content's
   * shadow-DOM container, not the window, so the browser's native anchor-scroll never
   * finds it (see class comment).
   */
  async scrollToId(id: string): Promise<void> {
    const target = document.getElementById(id);
    const content = this.content();
    if (!target || !content) return;

    const scrollEl = await content.getScrollElement();
    // Relative to the scroll container, not the viewport — the container is not at the
    // page origin (it sits below the header) and is itself already scrolled.
    const top = target.getBoundingClientRect().top - scrollEl.getBoundingClientRect().top
      + scrollEl.scrollTop - this.scrollOffset();
    await content.scrollToPoint(0, Math.max(top, 0), 300);
  }

  // ionScroll bubbles (composed: true) up to document across ion-content's shadow
  // boundary; the host page's <ion-content> needs [scrollEvents]="true" for it to fire.
  // Same mechanism PortalNavbarComponent uses for its scrolled state.
  @HostListener('document:ionScroll')
  onScroll(): void {
    // The section that has passed the offset line closest to it — i.e. the one being read.
    // Not "the last in list order": the index is grouped by product and need not follow
    // the order the cards appear in on the page.
    let active: string | null = null;
    let activeTop = -Infinity;
    for (const section of this.sections()) {
      const top = document.getElementById(section.id)?.getBoundingClientRect().top;
      if (top !== undefined && top <= this.scrollOffset() + 8 && top > activeTop) {
        active = section.id;
        activeTop = top;
      }
    }
    if (active === this.activeId) return; // most scroll frames end here — no CD churn
    this.activeId = active;
    this.cdr.markForCheck();
  }
}
